import { shallow } from '@liveblocks/client';
import { useEffect, useMemo, useCallback, useRef } from 'react';
import { v4 } from 'uuid';
import usePrevious from '@fabric-app/hooks/usePrevious';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { useExpandedResource, useShowExpandedResource, isExpandedResourceLocked } from '../../resources/stores/useExpandedResourceStore.js';
import { useUpdateSpacePresence, useSpaceSelf, useSpaceOthers, useBroadcastSpaceEvent, useSpaceLiveblocksStore, useSpaceEventListener } from '../config/spaces.config.js';
import { useSharedStateStore } from '../../store/sharedState.js';

const useMultiplayerFollow = (spaceId, type) => {
  const updateSpacePresence = useUpdateSpacePresence();
  const following = useSpaceSelf((me) => me.presence.following, shallow);
  const followingUser = useSpaceOthers((others2) => others2.find((o) => o.id === following), shallow);
  const previousFollowingUserPresence = usePrevious(followingUser?.presence);
  const others = useSpaceOthers();
  const self = useSpaceSelf();
  const broadcast = useBroadcastSpaceEvent();
  const accessToken = useContextAccessToken();
  const { enterRoom, leaveRoom, setClickEvents, setBroadcastClickEvent, setUserId, liveblocks } = useSpaceLiveblocksStore((state) => ({
    enterRoom: state.liveblocks.enterRoom,
    leaveRoom: state.liveblocks.leaveRoom,
    setClickEvents: state.setClickEvents,
    setBroadcastClickEvent: state.setBroadcastClickEvent,
    setUserId: state.setUserId,
    liveblocks: state.liveblocks
  }), shallow);
  useEffect(() => {
    const roomIdentificator = `${type}-${spaceId}`;
    enterRoom(roomIdentificator);
    return () => leaveRoom();
  }, [
    enterRoom,
    leaveRoom,
    spaceId,
    accessToken,
    liveblocks.room,
    type
  ]);
  useEffect(() => {
    setUserId(self?.id ?? "");
  }, [
    self?.id,
    setUserId
  ]);
  useEffect(() => {
    setBroadcastClickEvent((expandedResourceId2, position) => {
      broadcast({
        type: "userClick",
        expandedResourceId: expandedResourceId2,
        position
      });
    });
    return () => setBroadcastClickEvent(void 0);
  }, [
    broadcast,
    setBroadcastClickEvent
  ]);
  const { playingResourceId, mediaTimestamp, setPlayingResourceId, setMediaTimestamp, pdfPage, setPdfPage } = useSharedStateStore((state) => ({
    playingResourceId: state.playingResourceId,
    mediaTimestamp: state.mediaTimestamp,
    setPlayingResourceId: state.setPlayingResourceId,
    setMediaTimestamp: state.setMediaTimestamp,
    pdfPage: state.pdfPage,
    setPdfPage: state.setPdfPage
  }), shallow);
  const expandedResourceId = useExpandedResource();
  const showExpandedResource = useShowExpandedResource();
  useEffect(() => {
    updateSpacePresence({
      expandedResourceId
    });
  }, [
    expandedResourceId,
    updateSpacePresence
  ]);
  const { data: user } = useQueryUserInfo();
  const isLoggedIn = !!user;
  const liveMembers = useMemo(() => {
    const members = others.slice(0);
    if (self) members.push(self);
    return members;
  }, [
    others,
    self
  ]);
  const followUser = useCallback((userId, e, otherData) => {
    e?.preventDefault();
    e?.stopPropagation();
    const user2 = others.find((m) => m.id === userId);
    updateSpacePresence({
      following: following === userId ? null : userId,
      mediaPlaying: user2?.presence.mediaPlaying ?? false,
      mediaTimestamp: user2?.presence.mediaTimestamp ?? null,
      pdfPage: user2?.presence.pdfPage ?? null,
      ...otherData
    });
  }, [
    following,
    others,
    updateSpacePresence
  ]);
  const spotlightSelf = useCallback((_) => {
    broadcast({
      type: "spotlight",
      expandedResourceId: expandedResourceId ?? null,
      playingResourceId,
      mediaTimestamp,
      pdfPage
    });
  }, [
    broadcast,
    expandedResourceId,
    playingResourceId,
    mediaTimestamp,
    pdfPage
  ]);
  useEffect(() => {
    if (followingUser || !following) return;
    updateSpacePresence({
      following: null
    });
  }, [
    followingUser,
    following,
    updateSpacePresence
  ]);
  const previousExpandedResourceId = useRef(null);
  useEffect(() => {
    if (followingUser) return;
    previousExpandedResourceId.current = null;
  }, [
    followingUser
  ]);
  useEffect(() => {
    if (!followingUser) return;
    const followingMe = followingUser.presence.following === self?.id;
    if (!followingMe) return;
    followUser(null);
  }, [
    followingUser,
    followUser,
    self?.id
  ]);
  useSpaceEventListener(({ connectionId, event }) => {
    if (event.type === "spotlight") {
      if (isExpandedResourceLocked()) return;
      const user2 = others.find((m) => m.connectionId === connectionId);
      if (!user2) return;
      previousExpandedResourceId.current = event.expandedResourceId;
      showExpandedResource(event.expandedResourceId);
      followUser(user2.id, void 0, {
        playingResourceId: event.playingResourceId,
        mediaTimestamp: event.mediaTimestamp,
        pdfPage: event.pdfPage
      });
    } else if (event.type === "clearSpotlight") {
      followUser(null);
    } else if (event.type === "followPath") {
      if (isExpandedResourceLocked()) return;
      const { expandedResourceId: expandedResourceId2 } = event;
      previousExpandedResourceId.current = expandedResourceId2;
      showExpandedResource(expandedResourceId2);
    } else if (event.type === "userClick") {
      const other = others.find((u) => u.connectionId === connectionId);
      if (!other || other.presence.expandedResourceId !== expandedResourceId) return;
      setClickEvents((events) => [
        ...events,
        {
          id: v4(),
          position: event.position,
          user: other
        }
      ]);
    }
  });
  const allFollowingMeCount = useSpaceOthers((others2) => others2.filter((o) => o.presence.following === self?.id).length, shallow);
  useEffect(() => {
    if (!allFollowingMeCount) return;
    broadcast({
      type: "followPath",
      expandedResourceId: expandedResourceId ?? null
    });
  }, [
    allFollowingMeCount,
    broadcast,
    expandedResourceId
  ]);
  useEffect(() => {
    if (!followingUser) return;
    if (previousExpandedResourceId.current === expandedResourceId) return;
    followUser(null);
  }, [
    followUser,
    followingUser,
    expandedResourceId
  ]);
  const { playingResourceId: otherMediaPlayingResourceId, mediaTimestamp: otherMediaTimestamp, pdfPage: otherPdfPage } = followingUser?.presence ?? {};
  const { playingResourceId: previousMediaPlayingResourceId, mediaTimestamp: previousMediaTimestamp, pdfPage: previousPdfPage } = previousFollowingUserPresence ?? {};
  useEffect(() => {
    if (!followingUser) return;
    if (otherMediaPlayingResourceId === previousMediaPlayingResourceId && otherMediaPlayingResourceId !== playingResourceId) {
      followUser(null);
      return;
    }
    if (playingResourceId !== otherMediaPlayingResourceId) {
      setPlayingResourceId(followingUser.presence.playingResourceId);
    }
  }, [
    followUser,
    followingUser,
    playingResourceId,
    otherMediaPlayingResourceId,
    previousMediaPlayingResourceId,
    setPlayingResourceId
  ]);
  useEffect(() => {
    if (!followingUser) return;
    if (mediaTimestamp !== otherMediaTimestamp) {
      setMediaTimestamp(followingUser.presence.mediaTimestamp);
    }
  }, [
    followUser,
    followingUser,
    mediaTimestamp,
    otherMediaTimestamp,
    previousMediaTimestamp,
    setMediaTimestamp
  ]);
  useEffect(() => {
    updateSpacePresence({
      playingResourceId,
      mediaTimestamp,
      pdfPage
    });
  }, [
    playingResourceId,
    mediaTimestamp,
    updateSpacePresence,
    pdfPage
  ]);
  useEffect(() => {
    if (!followingUser) return;
    if (pdfPage !== otherPdfPage) {
      setPdfPage(followingUser.presence.pdfPage);
    }
  }, [
    followUser,
    followingUser,
    otherPdfPage,
    pdfPage,
    previousPdfPage,
    setPdfPage
  ]);
  useEffect(() => {
    if (expandedResourceId) return;
    setPlayingResourceId(null);
    setMediaTimestamp(null);
    setPdfPage(null);
  }, [
    expandedResourceId,
    setPlayingResourceId,
    setMediaTimestamp,
    setPdfPage
  ]);
  return {
    following,
    liveMembers,
    followUser,
    spotlightSelf,
    anonId: !isLoggedIn ? self?.id : void 0
  };
};

export { useMultiplayerFollow as default };
//# sourceMappingURL=useMultiplayerFollow.js.map
